<template>
  <div id="activatedTable" class="tracking-wrapper">
    <SlidingPanel :isPanelOpen="isPanelOpen" @closePanel="isPanelOpen = false">
      <template #content>
        <AddTalentPanel  @close="isPanelOpen = false"></AddTalentPanel>
      </template>
    </SlidingPanel>
    <div class="button-header">
      <Button class="icon icon-left primary light-text sm" :label="$t('app.campaign.follow.headerButtons.addTalent')" :disabled="campaign.archived" @click="goToNewTalent">
        <template #prefix>
          <PlusIcon style="width: 16px;"></PlusIcon>
        </template>
      </Button>
      <VDropdown :distance="10" :disabled="campaign.archived" placement="right">
        <button class="icon icon-left outline-white light-text sm" :class="{ disabled: campaign.archived }">
          <ActionsButtonIcon style="height: 14px;"></ActionsButtonIcon>
          <label>{{ $t('app.campaign.follow.headerButtons.actions.label') }}</label>
        </button>
        <template #popper>
          <button :disabled="anySelectedHasStartedAssessment"
            class="action red-color" v-close-popper
            @click="openActionModal([...state.selectedParticipants], 'delete')"
            v-tooltip="anySelectedHasStartedAssessment ? $t('app.campaign.follow.headerButtons.actions.removeTalentTooltip') : null"
          >
            <i class="bi bi-trash" />
            <span>{{ $t("app.campaign.activatedTable.deleteTalent") }}</span>
          </button>
          <button :disabled="!canActivateSelection"
            class="action green-color" v-close-popper
            @click="openActionModal([...state.selectedParticipants], 'activate')"
          >
            <ActivatedButtonIcon></ActivatedButtonIcon>
            <span>{{ $t("app.campaign.follow.headerButtons.activateAssessment") }}</span>
          </button>
          <button :disabled="!canDesactivateSelection"
            class="action orange-color" v-close-popper
            @click="openActionModal([...state.selectedParticipants], 'desactivate')"
          >
            <DesactivatedButtonIcon></DesactivatedButtonIcon>
            <span>{{ $t("app.campaign.follow.headerButtons.desactivateAssessment") }}</span>
          </button>
          <div class="separator" />
          <button :disabled="!noneHasFinished || !allSelectedActivated"
            class="action" v-close-popper @click="sendBulkReminder()"
            v-tooltip="!noneHasFinished || !allSelectedActivated ? $t('app.campaign.follow.headerButtons.actions.remindTalentTooltip') : null"
          >
            <i class="bi bi-arrow-counterclockwise" />
            <span>{{ $t('app.campaign.activatedTable.remindTalent') }}</span>
          </button>
          <button :disabled="anySelectedHasStartedAssessment"
            class="action" v-close-popper @click="openActionModal([...state.selectedParticipants], 'move')"
            v-tooltip="anySelectedHasStartedAssessment ? $t('app.campaign.follow.headerButtons.actions.moveTalentTooltip') : null"
          >
            <i class="bi bi-arrow-left-right" />
            <span>{{ $t("app.campaign.activatedTable.moveTalent") }}</span>
          </button>
        </template>
      </VDropdown>
      <VField class="put-to-end light sm white-background" :placeholder="$t('app.campaign.single.search')" name="search" icon="bi-search" v-model="state.search.value" @input="(event) => (state.search.value = event.target.value)" />
    </div>
    <div class="tracking-table">
      <TrackingTable
        :search="state.search"
        :filters="state.filters"
        @on-activate="executeAction($event, 'activate')"
        @on-desactivate="executeAction($event, 'desactivate')"
        @on-delete="executeAction($event, 'delete')"
        @on-move="executeAction($event, 'move')"
        @on-remind="sendReminderToParticipant"
      ></TrackingTable>
    </div>
    <ActionModal :modal="state.actionModal.modal" :data="state.participants" :isOpen="state.actionModal.show" @close="state.actionModal.show = false" @confirm="state.actionModal.show = false" />
    <HelpModal :isOpen="state.showHelpModal" @close="state.showHelpModal = !state.showHelpModal" />
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/style/variables.scss";

.tracking-wrapper {
  display: flex;
  flex-direction: column;
  flex: 1;
}

.tracking-table {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  margin-top: 16px;
  
  .xs-row {
    font-size: 14px;
    max-width: 100px;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap
  }

  .s-row {
    font-size: 14px;
    max-width: 140px;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap
  }

  .m-row {
    font-size: 14px;
    max-width: 160px;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap
  }

  .l-row {
    font-size: 14px;
    max-width: 210px;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap
  }
}

.center-field {
  justify-content: center !important;
  padding-top: 5px;
  padding-bottom: 5px;
}

.button-header {
  display: flex;
  flex-direction: row;
  gap: 8px;

  .put-to-end {
    margin-left: auto;
  }
}

.progress-bar-container .bar .progress {
  background-color: $blue-color;
}
</style>

<script setup lang="ts">
import { reactive, inject } from "vue";
import { useRoute } from "vue-router";
import { useToast } from "vue-toastification";

import ActionModal from "@/components/modals/app/actionModal/ActionModal.vue";
import HelpModal from "@/components/modals/app/helpModal/HelpModal.vue";
import Button from "@/components/global/Button.vue";
import TrackingTable from "@/components/global/tables/TrackingTable.vue";
import AddTalentPanel from "@/components/sliding-panel/campaign/AddTalentPanel.vue";
import SlidingPanel from "@/components/sliding-panel/SlidingPanel.vue";

import ActivatedButtonIcon from '@/assets/images/icon/campaign/follow/activate.svg?component';
import DesactivatedButtonIcon from '@/assets/images/icon/campaign/follow/desactivate.svg?component';
import ActionsButtonIcon from '@/assets/images/icon/campaign/action.svg?component';
import PlusIcon from '@/assets/images/icon/campaign/plus.svg?component';

import { useParticipations, useRemindParticipation } from "@/queries/participant.query";
import { useCampaign } from "@/queries/campaigns.query";

const route = useRoute();
const { t } = useI18n();

const moment = inject("moment");

const { data: participants } = useParticipations();
const { mutate: remindParticipation } = useRemindParticipation();
const { data: campaign } = useCampaign();

const isPanelOpen = ref(false);

const toast = useToast();

defineProps({
  search: {
    type: Object,
  },
});

const state = reactive({
  selectedParticipants: [],
  filters: {
    notActivated: false,
    accountNotCreated: false,
    pendingStart: false,
    inProgress: false,
    finished: false,
  },
  actionModal: {
    show: false,
    modal: "",
  },
  search: {
    value: "",
    searchable: [
      {
        combined: true,
        keys: ["pending_first_name", "pending_last_name"],
      },
      "pending_email",
      {
        combined: true,
        keys: ["candidate.first_name", "candidate.last_name"],
      },
      "candidate.email",
    ],
  },
  showHelpModal: false,
  participants: [],
});

function executeAction(participantId: string, actionType: 'activate' | 'desactivate' | 'delete' | 'move') {
  const participant = participants.value?.find((v) => v?.participant_id === participantId);
  if (participant) {
    state.participants = [participant];
    state.actionModal = {
      show: true,
      modal: actionType,
    };
  }
}

function openActionModal(participants, modal) {
  state.participants = participants;
  state.actionModal = {
    show: true,
    modal,
  };
}

async function sendBulkReminder() {
  for (const participant of state.selectedParticipants) {
    sendReminderToParticipant(participant);
  }
}

async function sendReminderToParticipant(participantId: string) {
  const participant = participants.value?.find((v) => v?.participant_id === participantId);
  const diff = moment().diff(moment.utc(participant.last_reminder_time, "YYYY/MM/DD HH:mm:ss")) / 1000;

  if (!isNaN(diff) && diff < 1800) {
    //if already sent a reminder in the last 30 minutes, don't event send an API call
    toast.info(t("app.campaign.activatedTable.toasts.recentReminder"));

    return;
  }


  remindParticipation({
    context: {
      organisationId: route.params.companyId as string,
      spaceId: route.params.spaceId as string,
      campaignId: route.params.campaignId as string,
    },
    participantId: participant.participant_id,
  }, {
    onSuccess: (reminder) => {
      if (reminder.sent) {
        toast.success(t("app.campaign.activatedTable.toasts.reminderSent"));
      } else {
        toast.info(t("app.campaign.activatedTable.toasts.recentReminder"));
      }
    },
    onError: () => {
      toast.error(t("app.campaign.activatedTable.toasts.reminderSendError"));
    }
  });

}

const canDesactivateSelection = computed(() => {
  if (state.selectedParticipants.length === 0) return false;
  return state.selectedParticipants.every((participant) => {
    return participant.activated && participant.state === 0;
  });
});

const canActivateSelection = computed(() => {
  if (state.selectedParticipants.length === 0) return false;
  return state.selectedParticipants.every((participant) => {
    return !participant.activated;
  });
});

const noneHasFinished = computed(() => {
  if (state.selectedParticipants.length === 0) return false;
  const someFinished = state.selectedParticipants.some((participant) => {
    return participant.state === 2;
  });
  return !someFinished;
});

const allSelectedActivated = computed(() => {
  if (state.selectedParticipants.length === 0) return false;
  return state.selectedParticipants.every((participant) => {
    return participant.activated;
  });
})

const anySelectedHasStartedAssessment = computed(() => {
  if (state.selectedParticipants.length === 0) return true;
  return state.selectedParticipants.some((participant) => {
    return participant.state === 1 || participant.state === 2;
  });
});

function goToNewTalent() {
  isPanelOpen.value = true;
}
</script>
